import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
    ArrowLeft, Users, Calendar, DollarSign, Briefcase, 
    UserPlus, Plus, Edit, Trash2, History, CreditCard, Settings
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminSalonDetailPage() {
    const { salonId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [staff, setStaff] = useState([]);
    const [services, setServices] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showStaffDialog, setShowStaffDialog] = useState(false);
    const [showServiceDialog, setShowServiceDialog] = useState(false);
    const [showSalonEditDialog, setShowSalonEditDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const [staffForm, setStaffForm] = useState({
        name: "",
        email: "",
        phone: "",
        role: "staff"
    });

    const [serviceForm, setServiceForm] = useState({
        name: "",
        description: "",
        price: 0,
        duration_minutes: 30,
        category: ""
    });

    const [salonForm, setSalonForm] = useState({
        business_name: "",
        owner_name: "",
        phone: "",
        address: "",
        description: ""
    });

    const token = localStorage.getItem('superadmin_token');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin/login');
            return;
        }
        loadAllData();
    }, [salonId]);

    const loadAllData = async () => {
        try {
            const [detailsRes, staffRes, servicesRes, transactionsRes, logsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/superadmin/salons/${salonId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/superadmin/salons/${salonId}/staff`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/superadmin/salons/${salonId}/services`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/superadmin/salons/${salonId}/transactions`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/api/superadmin/logs/activity?salon_id=${salonId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            setData(detailsRes.data);
            setStaff(staffRes.data);
            setServices(servicesRes.data);
            setTransactions(transactionsRes.data);
            setActivityLogs(logsRes.data);
            
            // Initialize salon form
            setSalonForm({
                business_name: detailsRes.data.salon.business_name,
                owner_name: detailsRes.data.salon.owner_name,
                phone: detailsRes.data.salon.phone || "",
                address: detailsRes.data.salon.address || "",
                description: detailsRes.data.salon.description || ""
            });
        } catch (error) {
            toast.error("Failed to load salon details");
            navigate('/superadmin');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSubscription = async (status) => {
        try {
            await axios.put(
                `${API_BASE}/api/superadmin/salons/${salonId}/subscription`,
                { status, days: 30 },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Subscription updated");
            loadAllData();
        } catch (error) {
            toast.error("Failed to update subscription");
        }
    };

    // Staff Management
    const handleSaveStaff = async (e) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await axios.put(
                    `${API_BASE}/api/superadmin/salons/${salonId}/staff/${editingItem.id}`,
                    staffForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Staff updated");
            } else {
                await axios.post(
                    `${API_BASE}/api/superadmin/salons/${salonId}/staff`,
                    staffForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Staff added");
            }
            setShowStaffDialog(false);
            setEditingItem(null);
            setStaffForm({ name: "", email: "", phone: "", role: "staff" });
            loadAllData();
        } catch (error) {
            toast.error("Failed to save staff");
        }
    };

    const handleEditStaff = (staff) => {
        setEditingItem(staff);
        setStaffForm({
            name: staff.name,
            email: staff.email || "",
            phone: staff.phone,
            role: staff.role
        });
        setShowStaffDialog(true);
    };

    const handleDeleteStaff = async (staffId, name) => {
        if (!confirm(`Delete staff member ${name}?`)) return;
        
        try {
            await axios.delete(
                `${API_BASE}/api/superadmin/salons/${salonId}/staff/${staffId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Staff deleted");
            loadAllData();
        } catch (error) {
            toast.error("Failed to delete staff");
        }
    };

    // Service Management
    const handleSaveService = async (e) => {
        e.preventDefault();
        try {
            if (editingItem) {
                await axios.put(
                    `${API_BASE}/api/superadmin/salons/${salonId}/services/${editingItem.id}`,
                    serviceForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Service updated");
            } else {
                await axios.post(
                    `${API_BASE}/api/superadmin/salons/${salonId}/services`,
                    serviceForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success("Service added");
            }
            setShowServiceDialog(false);
            setEditingItem(null);
            setServiceForm({
                name: "",
                description: "",
                price: 0,
                duration_minutes: 30,
                category: ""
            });
            loadAllData();
        } catch (error) {
            toast.error("Failed to save service");
        }
    };

    const handleEditService = (service) => {
        setEditingItem(service);
        setServiceForm({
            name: service.name,
            description: service.description || "",
            price: service.price,
            duration_minutes: service.duration_minutes,
            category: service.category || ""
        });
        setShowServiceDialog(true);
    };

    const handleDeleteService = async (serviceId, name) => {
        if (!confirm(`Delete service ${name}?`)) return;
        
        try {
            await axios.delete(
                `${API_BASE}/api/superadmin/salons/${salonId}/services/${serviceId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Service deleted");
            loadAllData();
        } catch (error) {
            toast.error("Failed to delete service");
        }
    };

    // Salon Details Update
    const handleUpdateSalonDetails = async (e) => {
        e.preventDefault();
        try {
            await axios.put(
                `${API_BASE}/api/superadmin/salons/${salonId}/details`,
                salonForm,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Salon details updated");
            setShowSalonEditDialog(false);
            loadAllData();
        } catch (error) {
            toast.error("Failed to update salon details");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const { salon, stats, recent_appointments } = data;

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                <Button variant="ghost" onClick={() => navigate('/superadmin')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Dashboard
                </Button>

                {/* Salon Info Header */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="font-heading text-2xl">{salon.business_name}</CardTitle>
                                <p className="text-muted-foreground">{salon.owner_name} • {salon.email}</p>
                                {salon.phone && <p className="text-sm text-muted-foreground">{salon.phone}</p>}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setShowSalonEditDialog(true)}>
                                    <Settings className="w-4 h-4 mr-2" />
                                    Edit Details
                                </Button>
                                <Select 
                                    value={salon.subscription_status} 
                                    onValueChange={handleUpdateSubscription}
                                >
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="trial">Trial</SelectItem>
                                        <SelectItem value="expired">Expired</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardHeader>
                </Card>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Total Clients</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.total_clients}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Appointments</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.total_appointments}</div>
                            <p className="text-xs text-muted-foreground">{stats.completed_appointments} completed</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Revenue</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{formatCurrency(stats.total_revenue)}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Staff</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats.total_staff}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs for different sections */}
                <Tabs defaultValue="staff" className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                        <TabsTrigger value="staff">Staff</TabsTrigger>
                        <TabsTrigger value="services">Services</TabsTrigger>
                        <TabsTrigger value="appointments">Appointments</TabsTrigger>
                        <TabsTrigger value="transactions">Transactions</TabsTrigger>
                        <TabsTrigger value="logs">Activity Logs</TabsTrigger>
                    </TabsList>

                    {/* Staff Tab */}
                    <TabsContent value="staff">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Staff Members</CardTitle>
                                    <Dialog open={showStaffDialog} onOpenChange={(open) => {
                                        setShowStaffDialog(open);
                                        if (!open) {
                                            setEditingItem(null);
                                            setStaffForm({ name: "", email: "", phone: "", role: "staff" });
                                        }
                                    }}>
                                        <DialogTrigger asChild>
                                            <Button>
                                                <UserPlus className="w-4 h-4 mr-2" />
                                                Add Staff
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>{editingItem ? "Edit Staff" : "Add New Staff"}</DialogTitle>
                                            </DialogHeader>
                                            <form onSubmit={handleSaveStaff} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Name *</Label>
                                                    <Input
                                                        value={staffForm.name}
                                                        onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Email</Label>
                                                    <Input
                                                        type="email"
                                                        value={staffForm.email}
                                                        onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Phone *</Label>
                                                    <Input
                                                        value={staffForm.phone}
                                                        onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Role</Label>
                                                    <Select value={staffForm.role} onValueChange={(value) => setStaffForm({...staffForm, role: value})}>
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="staff">Staff</SelectItem>
                                                            <SelectItem value="manager">Manager</SelectItem>
                                                            <SelectItem value="stylist">Stylist</SelectItem>
                                                            <SelectItem value="barber">Barber</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <Button type="submit" className="w-full">
                                                    {editingItem ? "Update Staff" : "Add Staff"}
                                                </Button>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {staff.map((member) => (
                                        <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{member.name}</p>
                                                <p className="text-sm text-muted-foreground">{member.phone}</p>
                                                {member.email && <p className="text-sm text-muted-foreground">{member.email}</p>}
                                                <Badge className="mt-1">{member.role}</Badge>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="ghost" onClick={() => handleEditStaff(member)}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteStaff(member.id, member.name)}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Services Tab */}
                    <TabsContent value="services">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Services</CardTitle>
                                    <Dialog open={showServiceDialog} onOpenChange={(open) => {
                                        setShowServiceDialog(open);
                                        if (!open) {
                                            setEditingItem(null);
                                            setServiceForm({
                                                name: "",
                                                description: "",
                                                price: 0,
                                                duration_minutes: 30,
                                                category: ""
                                            });
                                        }
                                    }}>
                                        <DialogTrigger asChild>
                                            <Button>
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Service
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>{editingItem ? "Edit Service" : "Add New Service"}</DialogTitle>
                                            </DialogHeader>
                                            <form onSubmit={handleSaveService} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Name *</Label>
                                                    <Input
                                                        value={serviceForm.name}
                                                        onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Description</Label>
                                                    <Input
                                                        value={serviceForm.description}
                                                        onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Price (GHS) *</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            value={serviceForm.price}
                                                            onChange={(e) => setServiceForm({...serviceForm, price: parseFloat(e.target.value)})}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Duration (mins) *</Label>
                                                        <Input
                                                            type="number"
                                                            value={serviceForm.duration_minutes}
                                                            onChange={(e) => setServiceForm({...serviceForm, duration_minutes: parseInt(e.target.value)})}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Category</Label>
                                                    <Input
                                                        value={serviceForm.category}
                                                        onChange={(e) => setServiceForm({...serviceForm, category: e.target.value})}
                                                        placeholder="e.g., Haircut, Styling, Color"
                                                    />
                                                </div>
                                                <Button type="submit" className="w-full">
                                                    {editingItem ? "Update Service" : "Add Service"}
                                                </Button>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {services.map((service) => (
                                        <div key={service.id} className="flex items-center justify-between p-4 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{service.name}</p>
                                                {service.description && (
                                                    <p className="text-sm text-muted-foreground">{service.description}</p>
                                                )}
                                                <div className="flex gap-2 mt-1">
                                                    <Badge variant="outline">{formatCurrency(service.price)}</Badge>
                                                    <Badge variant="outline">{service.duration_minutes} mins</Badge>
                                                    {service.category && <Badge>{service.category}</Badge>}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" variant="ghost" onClick={() => handleEditService(service)}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteService(service.id, service.name)}>
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Appointments Tab */}
                    <TabsContent value="appointments">
                        <Card>
                            <CardHeader>
                                <CardTitle>Recent Appointments</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {recent_appointments.map((appt) => (
                                        <div key={appt.id} className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                                            <div>
                                                <p className="font-medium">{appt.client_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {appt.service_names || appt.service_name} • {appt.date} at {appt.time}
                                                </p>
                                                {appt.staff_name && (
                                                    <p className="text-xs text-muted-foreground">Staff: {appt.staff_name}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <Badge>{appt.status}</Badge>
                                                <p className="text-sm font-medium mt-1">{formatCurrency(appt.service_price)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Transactions Tab */}
                    <TabsContent value="transactions">
                        <Card>
                            <CardHeader>
                                <CardTitle>Transaction History</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {transactions.map((txn) => (
                                        <div key={txn.id} className="flex items-center justify-between p-3 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{txn.description}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {txn.date} • {txn.payment_method || txn.category}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-medium ${txn.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                                    {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                                                </p>
                                                <Badge variant={txn.type === 'income' ? 'default' : 'secondary'}>
                                                    {txn.type}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Activity Logs Tab */}
                    <TabsContent value="logs">
                        <Card>
                            <CardHeader>
                                <CardTitle>Activity Logs</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {activityLogs.map((log) => (
                                        <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg">
                                            <History className="w-5 h-5 text-muted-foreground mt-0.5" />
                                            <div className="flex-1">
                                                <p className="font-medium">{log.description}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {new Date(log.created_at).toLocaleString()}
                                                </p>
                                            </div>
                                            <Badge variant="outline">{log.action}</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Salon Edit Dialog */}
                <Dialog open={showSalonEditDialog} onOpenChange={setShowSalonEditDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Salon Details</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleUpdateSalonDetails} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Business Name</Label>
                                <Input
                                    value={salonForm.business_name}
                                    onChange={(e) => setSalonForm({...salonForm, business_name: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Owner Name</Label>
                                <Input
                                    value={salonForm.owner_name}
                                    onChange={(e) => setSalonForm({...salonForm, owner_name: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Phone</Label>
                                <Input
                                    value={salonForm.phone}
                                    onChange={(e) => setSalonForm({...salonForm, phone: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Address</Label>
                                <Input
                                    value={salonForm.address}
                                    onChange={(e) => setSalonForm({...salonForm, address: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Input
                                    value={salonForm.description}
                                    onChange={(e) => setSalonForm({...salonForm, description: e.target.value})}
                                />
                            </div>
                            <Button type="submit" className="w-full">Update Salon Details</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}